/*
 * BNO homepage showcase carousel — PAGE-based.
 *
 * A deliberate split from the PDP showcase carousel (assets/bno-showcase-carousel.js).
 * The PDP one centers the nearest card and has one dot per card; this one shows a
 * fixed number of cards per view (2 on desktop, 1 on mobile) and paginates a whole
 * PAGE at a time, with one dot per PAGE.
 *
 *   - Pages by the visible count: page p shows cards [p*visible .. p*visible+visible-1].
 *   - Dots are generated in JS from ceil(cardCount / visible), so the count is
 *     derived (6 cards / 2 = 3 dots desktop; / 1 = 6 dots mobile) and updates
 *     automatically when cards are added — nothing is hardcoded.
 *   - Snapping is page-based: only page-start cards carry scroll-snap-align:start,
 *     so a swipe also lands on a page boundary.
 *   - Native horizontal scroll (touch/trackpad) + mouse drag; Left/Right arrows page.
 *   - prefers-reduced-motion: paging jumps instantly.
 *
 * Config via data attributes: data-visible-desktop (default 2), data-visible-mobile
 * (default 1). The desktop/mobile split matches the CSS 749px breakpoint.
 */
class BnoHomeShowcaseCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bno-home-showcase__track');
    this.cards = Array.from(this.querySelectorAll('.bno-home-showcase__card'));
    this.dotsWrap = this.querySelector('.bno-home-showcase__dots');

    if (!this.track || this.cards.length === 0) return;

    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mqMobile = window.matchMedia('(max-width: 749px)');
    this.visibleDesktop = parseInt(this.dataset.visibleDesktop || '2', 10) || 2;
    this.visibleMobile = parseInt(this.dataset.visibleMobile || '1', 10) || 1;
    this.page = 0;
    this.dots = [];
    /** @type {number | null} */
    this.scrollRaf = null;

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);

    // Clicking a peeking card pages to that card's page (a drag is not a click —
    // the capture-phase blocker in #setupDrag stops it when the pointer moved).
    this.cards.forEach((card, i) => {
      card.addEventListener('click', () => this.goToPage(Math.floor(i / this.visible())));
    });

    this.#setupDrag();

    this.onBreakpoint = () => this.layout();
    this.mqMobile.addEventListener
      ? this.mqMobile.addEventListener('change', this.onBreakpoint)
      : this.mqMobile.addListener(this.onBreakpoint);

    this.layout();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.mqMobile) {
      this.mqMobile.removeEventListener
        ? this.mqMobile.removeEventListener('change', this.onBreakpoint)
        : this.mqMobile.removeListener(this.onBreakpoint);
    }
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  visible() {
    return this.mqMobile.matches ? this.visibleMobile : this.visibleDesktop;
  }

  pageCount() {
    return Math.max(1, Math.ceil(this.cards.length / this.visible()));
  }

  /** Re-run on load and whenever the breakpoint (visible count) changes. */
  layout() {
    const vis = this.visible();
    // Page-based snapping: only every vis-th card (a page start) snaps.
    this.cards.forEach((card, i) => {
      card.style.scrollSnapAlign = i % vis === 0 ? 'start' : 'none';
    });
    this.buildDots();
    this.page = Math.min(this.page, this.pageCount() - 1);
    this.syncActive();
  }

  /** (Re)build the dots to match the current page count. */
  buildDots() {
    if (!this.dotsWrap) return;
    const n = this.pageCount();
    if (this.dots.length !== n) {
      this.dotsWrap.innerHTML = '';
      this.dots = [];
      for (let i = 0; i < n; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bno-home-showcase__dot';
        b.setAttribute('aria-label', `Go to page ${i + 1} of ${n}`);
        b.addEventListener('click', () => this.goToPage(i));
        this.dotsWrap.appendChild(b);
        this.dots.push(b);
      }
    }
    // Hide the dots pill entirely when there is only one page.
    this.dotsWrap.hidden = n <= 1;
  }

  /** The track's snap inset (content-column left), in px. */
  #padStart() {
    return parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
  }

  /** Scroll so page p's first card rests at the content-column left edge. */
  goToPage(p) {
    const pages = this.pageCount();
    p = Math.max(0, Math.min(pages - 1, p));
    this.page = p;
    const idx = Math.min(p * this.visible(), this.cards.length - 1);
    const card = this.cards[idx];
    if (card) {
      const left =
        this.track.scrollLeft +
        (card.getBoundingClientRect().left - this.track.getBoundingClientRect().left) -
        this.#padStart();
      this.track.scrollTo({ left: Math.max(0, left), behavior: this.reduce.matches ? 'auto' : 'smooth' });
    }
    this.syncActive();
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateActiveFromScroll();
    });
  }

  /** Keep the active page in sync with manual swipes (nearest page-start card). */
  updateActiveFromScroll() {
    const vis = this.visible();
    const target = this.track.getBoundingClientRect().left + this.#padStart();
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.cards.length; i += vis) {
      const d = Math.abs(this.cards[i].getBoundingClientRect().left - target);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    this.page = best / vis;
    this.syncActive();
  }

  syncActive() {
    this.dots.forEach((dot, i) => {
      const active = i === this.page;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToPage(this.page + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToPage(this.page - 1);
    }
  }

  /** Mouse drag-to-scroll. Touch keeps native scrolling. */
  #setupDrag() {
    const track = this.track;
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let captured = false;
    let pointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      captured = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        track.style.scrollSnapType = 'none';
        try {
          track.setPointerCapture(pointerId);
          captured = true;
        } catch (_) {
          /* ignore */
        }
      }
      if (moved) track.scrollLeft = startScroll - dx;
    });

    const end = () => {
      if (!down) return;
      down = false;
      if (moved) {
        track.style.scrollSnapType = '';
        if (captured) {
          try {
            track.releasePointerCapture(pointerId);
          } catch (_) {
            /* ignore */
          }
        }
        requestAnimationFrame(() => this.updateActiveFromScroll());
      }
    };

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }
}

if (!customElements.get('bno-home-showcase-carousel')) {
  customElements.define('bno-home-showcase-carousel', BnoHomeShowcaseCarousel);
}
